import { Component, EventEmitter, Input, Output, OnDestroy, OnInit, OnChanges } from '@angular/core';
import { UnitSystem } from 'src/app/shared/enums/unit-system.enum';
import { UnitConversionService, UNITS } from 'src/app/shared/services/unit-conversion.service';
import { ADVANCEDPIPE_MAX_MIN_VALIDATORS, getAdvancedPipeMaxMinValidator, ADVANCEDPIPE_DECIMAL_PLACES_VALIDATORS } from '../../advancedpipe-validators.constants';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-advancedpipe-ranges-modal',
    templateUrl: './advancedpipe-ranges-modal.component.html',
    styleUrls: ['./advancedpipe-ranges-modal.component.css']
})
export class AdvancedpipeRangesModalComponent implements OnInit, OnDestroy, OnChanges {
    @Input() show: boolean = false;
    @Input() unitSystem: UnitSystem = UnitSystem.USC;
    @Output() close = new EventEmitter<void>();

    private unitSystemSubscription: Subscription;

    // Range values
    outsideDiameterMin: number;
    outsideDiameterMax: number;
    outsideDiameterUnit: string;
    wallThicknessMin: number;
    wallThicknessMax: number;
    wallThicknessUnit: string;
    minWallThicknessMin: number = ADVANCEDPIPE_MAX_MIN_VALIDATORS.PIPE_WT_REDUCTION_MIN;
    minWallThicknessMax: number = ADVANCEDPIPE_MAX_MIN_VALIDATORS.PIPE_WT_REDUCTION_MAX;
    maxFlawSizeMin: number;
    maxFlawSizeMax: number;
    maxFlawSizeUnit: string;
    materialToughnessMin: number;
    materialToughnessMax: number;
    ovalityMin: number = ADVANCEDPIPE_MAX_MIN_VALIDATORS.OVALITY_MIN;
    ovalityMax: number = ADVANCEDPIPE_MAX_MIN_VALIDATORS.OVALITY_MAX;
    eccentricityMin: number = ADVANCEDPIPE_MAX_MIN_VALIDATORS.ECCENTRICITY_MIN;
    eccentricityMax: number = ADVANCEDPIPE_MAX_MIN_VALIDATORS.ECCENTRICITY_MAX;
    residualStressMin: number;
    residualStressMax: number;
    residualStressUnit: string;
    internalPressureMin: number;
    internalPressureUnit: string;
    externalPressureMin: number;
    externalPressureUnit: string;
    
    // Fixed values based on provided data
    smysMin: number = 35; // USC: ksi
    smysMax: number = 150; // USC: ksi
    smysUnit: string;
    utsMin: number = 40; // USC: ksi
    utsMax: number = 160; // USC: ksi
    utsUnit: string;
    youngModulusMin: number = 25000; // USC: ksi
    youngModulusMax: number = 30000; // USC: ksi
    youngModulusUnit: string;
    poissonRatioMin: number = 0.27;
    poissonRatioMax: number = 0.33;

    constructor(private unitConversionService: UnitConversionService) { }

    /**
     * Rounds a value to the configured decimal places for a field
     */
    private roundToConfiguredDecimals(value: number, fieldName: string): number {
        const decimals = ADVANCEDPIPE_DECIMAL_PLACES_VALIDATORS[fieldName]?.[this.unitSystem] ?? 2;
        return Number(value.toFixed(decimals));
    }

    /**
     * Gets the decimal places configuration for UTS field
     */
    getUtsDecimalPlaces(): string {
        const decimals = ADVANCEDPIPE_DECIMAL_PLACES_VALIDATORS.uts?.[this.unitSystem] ?? 1;
        return `1.${decimals}-${decimals}`;
    }

    /**
     * Gets the decimal places configuration for SMYS field
     */
    getSmysDecimalPlaces(): string {
        const decimals = ADVANCEDPIPE_DECIMAL_PLACES_VALIDATORS.smys?.[this.unitSystem] ?? 1;
        return `1.${decimals}-${decimals}`;
    }

    ngOnInit() {
        this.updateRanges();
    }

    ngOnDestroy() {
        if (this.unitSystemSubscription) {
            this.unitSystemSubscription.unsubscribe();
        }
    }

    private updateRanges() {
        // Outside Diameter
        this.outsideDiameterMin = getAdvancedPipeMaxMinValidator('OUTSIDE_DIAMETER_MIN', this.unitSystem);
        this.outsideDiameterMax = getAdvancedPipeMaxMinValidator('OUTSIDE_DIAMETER_MAX', this.unitSystem);
        this.outsideDiameterUnit = this.unitSystem === UnitSystem.USC ? UNITS.LENGTH_IN : UNITS.LENGTH_MM;

        // Wall Thickness
        this.wallThicknessMin = getAdvancedPipeMaxMinValidator('WALL_THICKNESS_MIN', this.unitSystem);
        this.wallThicknessMax = getAdvancedPipeMaxMinValidator('WALL_THICKNESS_MAX', this.unitSystem);
        this.wallThicknessUnit = this.unitSystem === UnitSystem.USC ? UNITS.LENGTH_IN : UNITS.LENGTH_MM;

        // Max Flaw Size
        this.maxFlawSizeMin = getAdvancedPipeMaxMinValidator('MAX_FLAW_SIZE_MIN', this.unitSystem);
        this.maxFlawSizeMax = getAdvancedPipeMaxMinValidator('MAX_FLAW_SIZE_MAX', this.unitSystem);
        this.maxFlawSizeUnit = this.unitSystem === UnitSystem.USC ? UNITS.LENGTH_IN : UNITS.LENGTH_MM;

        // Material Toughness
        this.materialToughnessMin = getAdvancedPipeMaxMinValidator('MATERIAL_TOUGHNESS_MIN', this.unitSystem);
        this.materialToughnessMax = getAdvancedPipeMaxMinValidator('MATERIAL_TOUGHNESS_MAX', this.unitSystem);

        // Residual Stress
        this.residualStressMin = getAdvancedPipeMaxMinValidator('RESIDUAL_STRESS_MIN', this.unitSystem);
        this.residualStressMax = getAdvancedPipeMaxMinValidator('RESIDUAL_STRESS_MAX', this.unitSystem);
        this.residualStressUnit = this.unitSystem === UnitSystem.USC ? '-' : '-'; // Dimensionless

        // Internal Pressure
        this.internalPressureMin = getAdvancedPipeMaxMinValidator('INTERNAL_PRESSURE_MIN', this.unitSystem);
        this.internalPressureUnit = this.unitSystem === UnitSystem.USC ? UNITS.PRESSURE_KSI : UNITS.PRESSURE_MPA;

        // External Pressure
        this.externalPressureMin = getAdvancedPipeMaxMinValidator('EXTERNAL_PRESSURE_MIN', this.unitSystem);
        this.externalPressureUnit = this.unitSystem === UnitSystem.USC ? UNITS.PRESSURE_KSI : UNITS.PRESSURE_MPA;

        // Convert fixed values to appropriate units
        if (this.unitSystem === UnitSystem.SI) {
            // SMYS conversion with proper decimal rounding (35-150 ksi)
            const smysMinConverted = this.unitConversionService.convert(35, UNITS.PRESSURE_KSI, UNITS.PRESSURE_MPA);
            const smysMaxConverted = this.unitConversionService.convert(150, UNITS.PRESSURE_KSI, UNITS.PRESSURE_MPA);
            this.smysMin = this.roundToConfiguredDecimals(smysMinConverted, 'smys');
            this.smysMax = this.roundToConfiguredDecimals(smysMaxConverted, 'smys');
            
            // UTS conversion with proper decimal rounding
            const utsMinConverted = this.unitConversionService.convert(40, UNITS.PRESSURE_KSI, UNITS.PRESSURE_MPA);
            const utsMaxConverted = this.unitConversionService.convert(160, UNITS.PRESSURE_KSI, UNITS.PRESSURE_MPA);
            this.utsMin = this.roundToConfiguredDecimals(utsMinConverted, 'uts');
            this.utsMax = this.roundToConfiguredDecimals(utsMaxConverted, 'uts');
            
            const youngModulusMinConverted = this.unitConversionService.convert(25000, UNITS.PRESSURE_KSI, UNITS.PRESSURE_MPA);
            const youngModulusMaxConverted = this.unitConversionService.convert(30000, UNITS.PRESSURE_KSI, UNITS.PRESSURE_MPA);
            this.youngModulusMin = this.roundToConfiguredDecimals(youngModulusMinConverted, 'youngModulus');
            this.youngModulusMax = this.roundToConfiguredDecimals(youngModulusMaxConverted, 'youngModulus');
        } else {
            this.smysMin = 35;
            this.smysMax = 150;
            this.utsMin = 40;
            this.utsMax = 160;
            this.youngModulusMin = 25000;
            this.youngModulusMax = 30000;
        }

        this.smysUnit = this.unitSystem === UnitSystem.USC ? 'ksi' : UNITS.PRESSURE_MPA;
        this.utsUnit = this.unitSystem === UnitSystem.USC ? UNITS.PRESSURE_KSI : UNITS.PRESSURE_MPA;
        this.youngModulusUnit = this.unitSystem === UnitSystem.USC ? 'ksi' : UNITS.PRESSURE_MPA;
    }

    ngOnChanges() {
        this.updateRanges();
    }

    closeModal() {
        this.close.emit();
    }
}